import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { ChevronUpIcon, ChevronDownIcon, PencilIcon, TrashIcon } from '@heroicons/react/solid'

import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import ImageUploadForm from '@/shared/ImageUploadFormR2'
import useModal from '@/hooks/useModalV2'
import Loading from '@/shared/Loading'
import StatusPill from '@/shared/StatusPill'
import Modal from '@/shared/Modal'
import classNames from '@/utils/classNamesLocal'

import RoleEdit from '@/pages/ManageProjects/ProjectEdit/RoleEdit'

const RoleImage = ({ role }) => {
  const [url, setUrl] = useState(role.imageUrl)
  const [uploaded, setUploaded] = useState(false)
  const { openModal, closeModal, isOpen } = useModal()
  const { getRequest } = useQuery()

  const reloadPublicImageURL = () => {
    getRequest(`/api/v3/roles/${role.id}/image`, {}, (err, jsonData) => {
      if (err) { return }
      setUrl(jsonData.url)
    })
  }

  return <>
    <button type='button' onClick={openModal} title='Change picture' className='relative flex-shrink-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-cccblue'>
      <img className='h-14 w-14 rounded-lg object-cover ring-2 ring-gray-200 hover:opacity-80 dark:ring-gray-600' src={url} alt={role.name} />
      { uploaded && <span className='absolute inset-0 flex items-center justify-center'><Loading noMessage noLoadingMessage /></span> }
    </button>
    <Modal isOpen={isOpen} closeModal={closeModal}>
      <h3 className='text-lg font-medium leading-6 text-gray-900 dark:text-white'>Role picture</h3>
      <div className='mt-2 flex justify-center'>
        <ImageUploadForm kind='Role' roleId={role.id} url={url} setUrl={setUrl} success={reloadPublicImageURL} didUpload={setUploaded} aspect='square' />
      </div>
    </Modal>
  </>
}

const IconButton = ({ onClick, label, disabled, danger, children }) => (
  <button type='button' onClick={onClick} title={label} aria-label={label} disabled={disabled}
    className={classNames('rounded-md p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-cccblue',
      disabled ? 'cursor-not-allowed text-gray-300 dark:text-gray-600'
        : danger ? 'text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-900/30'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white')}>
    {children}
  </button>
)

const RoleRow = ({ initialRole, idx, count, onMoveUp, onMoveDown, onDeleted, embedded }) => {
  const [, setToast] = useGlobalState('toast')
  const { putpostRequest } = useQuery()
  const [role, setRole] = useState(initialRole)
  const [editing, setEditing] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const pillColor = role.status === 'open' ? 'warning' : role.status === 'closed' ? 'success' : 'default'

  // The row only disappears once the server answers, and Delete/Really Delete?
  // swap in the same spot, so a double click lands on the confirm and sends the
  // DELETE twice. The second one 404s on a role the first one already destroyed,
  // and useQuery throws the whole SPA to the error page for a delete that worked.
  const destroy = () => {
    setDeleting(true)
    putpostRequest(`/api/v3/roles/${role.id}`, 'DELETE', {}, (err, jsonData) => {
      // Every refusal from #destroy (auditions, assigned talent, last live role)
      // arrives here, not below: useQuery hands 422 errors back as `err`.
      if (err) { setDeleting(false); return }
      if (jsonData.errors) {
        setDeleting(false)
        setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
          <p className='text-sm font-medium text-red-500'>That role is staying put</p>
          <p className='mt-1 text-sm text-red-500'>{jsonData.errors}</p>
        </div>)
        return
      }
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium text-gray-800 dark:text-white'>Role deleted</p>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-100'>Please. A moment of silence. ...okay, I'm good now.</p>
      </div>)
      onDeleted()
    })
  }

  return <li className='rounded-xl bg-white shadow-sm dark:bg-gray-800'>
    <div className='flex items-center gap-x-3 p-3'>
      <div className='flex flex-col'>
        <IconButton label='Move up' onClick={onMoveUp} disabled={idx === 0}><ChevronUpIcon className='h-4 w-4' /></IconButton>
        <IconButton label='Move down' onClick={onMoveDown} disabled={idx === count - 1}><ChevronDownIcon className='h-4 w-4' /></IconButton>
      </div>
      <RoleImage role={role} />
      <div className='min-w-0 flex-1'>
        <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
          <span className='truncate font-semibold text-gray-900 dark:text-white'>{role.name}</span>
          <span className='rounded-md bg-cccblue/15 px-2 py-0.5 text-xs font-medium text-cccblue'>{role.kind}</span>
          { !embedded && <StatusPill status={pillColor} text={role.status} /> }
        </div>
        { !embedded && <Link to={`/manage/projects/${role.projectId}/roles`} className='text-xs text-cccblue hover:underline'>Manage auditions</Link> }
      </div>
      <div className='flex flex-shrink-0 items-center gap-x-1'>
        { deleteConfirm
          ? <button type='button' onClick={destroy} disabled={deleting} className='rounded-md bg-red-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50'>
            { deleting ? 'Poofing...' : 'Really delete?' }
          </button>
          : <IconButton label='Delete role' danger onClick={() => setDeleteConfirm(true)}><TrashIcon className='h-4 w-4' /></IconButton> }
        <button type='button' onClick={() => setEditing(!editing)} aria-expanded={editing}
          className={classNames('inline-flex items-center gap-x-1 rounded-full px-3 py-1 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cccblue',
            editing ? 'bg-cccblue text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600')}>
          <PencilIcon className='h-3.5 w-3.5' /> { editing ? 'Close' : 'Edit' }
        </button>
      </div>
    </div>
    { editing && <div className='border-t border-gray-100 p-4 dark:border-gray-700'>
      <RoleEdit initialRole={role} updateRole={setRole} closeModal={() => setEditing(false)} />
    </div> }
  </li>
}

export default RoleRow

RoleRow.propTypes = {
  initialRole: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
  embedded: PropTypes.bool
}
